import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Lightbulb, Award, Globe, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Outcome-Focused",
      description: "We measure success by your business results, not project deliverables."
    },
    {
      icon: Lightbulb,
      title: "Innovation-Driven",
      description: "Constantly evolving with AI and emerging technologies to stay ahead."
    },
    {
      icon: Users,
      title: "Partnership Mindset",
      description: "Your success is our success. We're invested in your long-term growth."
    },
    {
      icon: Award,
      title: "Excellence Standard",
      description: "Certified experts delivering world-class solutions consistently."
    }
  ];

  const timeline = [
    { year: "2018", event: "Founded with vision to simplify enterprise IT" },
    { year: "2020", event: "Expanded to 50+ enterprise clients globally" },
    { year: "2022", event: "Launched AI-first delivery framework" },
    { year: "2024", event: "Reached 150+ clients across 25 countries" },
    { year: "2030", event: "Vision: Leading AI-powered enterprise platform" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-orange-200/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Building the Future of
              <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Enterprise Technology</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              AiFlo.ai was born from a simple insight: enterprises don't need more vendors—they need one intelligent partner who can unify their technology ecosystem and deliver measurable outcomes.
            </p>
            <Link to="/contact">
              <Button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl shadow-xl">
                Work With Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="p-10 bg-white hover:shadow-2xl transition-all duration-300">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To eliminate enterprise fragmentation by providing a unified, AI-first platform that connects every system, accelerates delivery, and delivers tangible business value.
              </p>
            </Card>

            <Card className="p-10 bg-gradient-to-br from-gray-900 to-gray-800 text-white hover:shadow-2xl transition-all duration-300">
              <div className="w-14 h-14 bg-orange-400/20 rounded-2xl flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-orange-400" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Vision 2030</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                To be the world's most trusted AI-powered enterprise platform, enabling organizations to achieve breakthrough innovation through intelligent automation and seamless integration.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">
              Principles that guide every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-orange-200"
              >
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-orange-50/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">
              From startup to enterprise partner of choice
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-400 to-gray-400" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <Card className="inline-block p-6 bg-white hover:shadow-xl transition-all duration-300">
                      <div className="text-2xl font-bold text-orange-600 mb-2">{item.year}</div>
                      <p className="text-gray-700">{item.event}</p>
                    </Card>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-orange-400 rounded-full border-4 border-white shadow-lg" />
                  </div>
                  
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Enterprises Trust AiFlo.ai
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Single Point of Accountability",
                    description: "No more juggling multiple vendors. One partner, one platform, complete ownership."
                  },
                  {
                    title: "AI-Augmented Delivery",
                    description: "Humans lead, AI amplifies. Faster delivery without compromising quality."
                  },
                  {
                    title: "Platform Agnostic Approach",
                    description: "We recommend what's best for your business, not what earns us the highest commission."
                  },
                  {
                    title: "Proven Track Record",
                    description: "150+ successful enterprise transformations across 25 countries."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Heart className="w-5 h-5 text-orange-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-orange-100 to-gray-100 rounded-3xl overflow-hidden">
                <div className="w-full h-full flex items-center justify-center p-12">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-gray-900 mb-4">150+</div>
                    <p className="text-xl text-gray-600">Enterprise Clients Worldwide</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-orange-400 rounded-3xl blur-2xl opacity-30" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-gray-400 rounded-3xl blur-2xl opacity-20" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-orange-400/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Join 150+ Enterprises on Their Transformation Journey
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's explore how AiFlo.ai can accelerate your digital transformation.
          </p>
          <Link to="/contact">
            <Button className="bg-orange-400 hover:bg-orange-500 text-white px-8 py-6 text-lg rounded-xl shadow-2xl hover:scale-105 transition-all duration-300">
              Start the Conversation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
